<template>
  <div>
    <header
      v-if="node"
      class="search-page-header bg-pink bg-gradient-to-r from-green to-green-light pt-40 pb-70 xl:pt-60 xl:pb-90 relative logo-background overflow-hidden text-center"
    >
      <h1 class="text-4xl xl:text-5xl text-white">
        <span class="font-semibold">{{ node.title }}</span>
      </h1>
      <div
        v-if="node.body"
        class="text-white text-md xl:text-lg max-w-hero mx-auto mt-20"
        v-html="node.body"
      />
    </header>
    <SearchField v-model.lazy="text">
      <div v-if="data?.didYouMean" class="text-lg mt-20">
        {{ $texts('search.didYouMean', 'Meinten Sie:') }}
        <NuxtLink
          class="link font-semibold"
          :to="{ name: 'search', query: { text: data.didYouMean } }"
          >{{ data.didYouMean }}</NuxtLink
        >
      </div>
      <div class="buttons is-overflow mt-40">
        <ButtonStyle
          v-for="type in entityTypes"
          :key="type.key"
          v-slot="{ classes }"
          :inverted="selectedEntityType === type.key"
        >
          <NuxtLink :to="getFilterUrl(type)" :class="classes">
            <span>{{ getTypeLabel(type.key) }}</span>
            <span
              class="inline-block ml-10 w-20 h-20 bg-white/20 rounded leading-none"
              >{{ type.count }}</span
            >
          </NuxtLink>
        </ButtonStyle>
      </div>
    </SearchField>
    <Container
      v-if="searchTitle"
      :class="{ 'opacity-20 pointer-events-none': pending }"
    >
      <h2 class="font-semibold text-xl text-center mb-40">
        {{ searchTitle }}
      </h2>

      <SearchPopularServices v-if="shouldShowPopularServices" />

      <SearchOnPharmago v-if="siteName === 'pharmago' && text" :text="text" />

      <div
        v-if="results?.length"
        ref="scrollToEl"
        class="grid grid-cols-1 gap-20 lg:gap-30 scroll-mt-40"
      >
        <DrupalLink
          v-for="item in results"
          :key="item.id"
          :to="item.url"
          class="box bg-white flex items-center"
        >
          <div
            class="bg-gray-100 relative w-50 md:w-120 lg:w-200 aspect-square flex-shrink-0 hidden md:block"
          >
            <RokkaImage
              v-if="item.teaserImage"
              :hash="item.teaserImage.rokka.hash"
              :config="imageStyle"
            />
          </div>

          <div class="box-padding">
            <p
              v-if="item.type"
              class="uppercase text-xs text-green font-semibold"
            >
              {{ item.type }}
            </p>
            <h2
              class="font-semibold md:text-lg search-excerpt"
              v-html="item.title"
            />
            <p
              v-if="item.body"
              class="mt-10 search-excerpt line-clamp-2"
              v-html="item.body"
            />
            <SpriteSymbol
              v-if="item.isPaywalled"
              name="lock"
              class="mr-5 h-20 w-20 text-green absolute top-20 right-20"
            />
          </div>
        </DrupalLink>
      </div>

      <Pagination
        v-if="totalPages > 1"
        :page="page"
        :total-pages="totalPages"
        class="mt-40"
        @change-page="onChangePage"
      />
    </Container>
  </div>
</template>

<script lang="ts" setup>
import type {
  ElasticsearchAggregationBucketFragment,
  NodePageFragment,
  SearchContentQuery,
} from '#graphql-operations'

definePageMeta({
  name: 'search',
  drupalRoute: true,
  languageMapping: {
    de: '/de/suche',
    fr: '/fr/recherche',
    it: '/it/ricerca',
  },
})

const nuxtRoute = useRoute()
const router = useRouter()
const { $texts } = useNuxtApp()
const { name: siteName } = useCurrentSite()

const queryStringText = useQueryString('text')
const queryStringPage = useQueryString('page')
const selectedEntityType = useQueryString('type')

const page = computed(() => {
  return queryStringPage.value ? parseInt(queryStringPage.value) : 0
})

const text = computed({
  get() {
    return queryStringText.value
  },
  set(v: string) {
    if (!v) {
      return router.push({ query: {} })
    }
    router.push({ query: { text: v } })
  },
})

const { data: query } = await useAsyncGraphqlQuery(
  'route',
  {
    path: nuxtRoute.path,
  },
  {
    transform: (v) => v.data,
    graphqlCaching: {
      client: true,
    },
  },
)

// Handles redirects and metatags.
const { entity: node } = await useDrupalRoute<NodePageFragment>(query.value)

const { data, pending, error } = await useAsyncData<
  SearchContentQuery['searchContent'] | null
>(
  () => {
    if (!text.value) {
      return Promise.resolve(null)
    }
    return useGraphqlQuery(
      'searchContent',
      {
        text: text.value,
        page: page.value,
        entityType: selectedEntityType.value,
      },
      {
        graphqlCaching: {
          client: true,
        },
      },
    ).then((v) => {
      if (!v.data.searchContent) {
        throw new Error('Search query failed.')
      }

      // Sends search data to GTM.
      if (import.meta.client && text.value && window.dataLayer) {
        window.dataLayer.push({
          event: 'view_search_results',
          searchTerm: text.value,
          searchFilter: selectedEntityType.value,
        })
      }

      return v.data.searchContent
    })
  },
  { watch: [text, page, selectedEntityType] },
)

const throwSearchNotAvailable = () => {
  throw createError({
    statusCode: 500,
    fatal: true,
    statusMessage: $texts(
      'search.elasticSearchNotAvailableTitle',
      'Suche nicht verfügbar',
    ),
    message: $texts(
      'search.elasticSearchNotAvailableText',
      'Die Suche steht zur Zeit nicht zur Verfügung. Bitte versuchen Sie es später noch einmal.',
    ),
  })
}

if (error.value) {
  throwSearchNotAvailable()
}

watch(error, throwSearchNotAvailable)

const results = computed(() => {
  return (data.value?.hits || []).map((v) => {
    return {
      id: v.source?.id,
      title: v.highlight?.title || v.source?.title,
      body:
        v.highlight?.markup ||
        v.highlight?.body ||
        v.source?.body ||
        v.source?.summary,
      url: v.source?.url,
      teaserImageHash: v.source?.teaserImageHash,
      bundle: v.source?.bundle,
      type: getTypeLabel(v.source?.type),
      score: v.score,
      isPaywalled: v.source?.isPaywalled,
      teaserImage: v.source?.teaserImageHash
        ? {
            label: v.source?.teaserImageHash + '.png',
            rokka: {
              hash: v.source?.teaserImageHash,
            },
          }
        : null,
    }
  })
})

const entityTypes = computed<ElasticsearchAggregationBucketFragment[]>(() => {
  return (
    data.value?.aggs?.find((v) => v.name === 'entity_types')?.buckets || []
  ).sort((a, b) => {
    const aKey = a.key || ''
    const bKey = b.key || ''
    return bKey > aKey ? -1 : 1
  })
})

const total = computed(() => {
  return data.value?.total || 0
})

const perPage = 12

const totalPages = computed(() => {
  return total.value ? Math.ceil(total.value / perPage) : 0
})

const searchTitle = computed(() => {
  if (!text.value || data.value === null) {
    return
  }
  if (!total.value && !pending.value) {
    return $texts(
      'search.titleNoResults',
      'Keine Resultate für den Suchbegriff «@term»',
    ).replace('@term', text.value)
  }
  return $texts(
    'search.titleResults',
    '@count Resultate für den Suchbegriff «@term»',
  )
    .replace('@count', total.value.toString())
    .replace('@term', text.value)
})

const entityTypeLabels = computed<Record<string, string>>(() => {
  return {
    article: $texts('search.typeArticle', 'Artikel'),
    document: $texts('search.typeDocument', 'Dokument'),
    event: $texts('search.typeEvent', 'Event'),
    product: $texts('search.typeProduct', 'Produkt'),
    press_release: $texts('search.typePressRelease', 'Medienmitteilung'),
    page: $texts('search.typePage', 'Seite'),
  }
})

function getTypeLabel(v?: string): string {
  if (!v) {
    return ''
  }
  return entityTypeLabels.value[v] || ''
}

function getFilterUrl(v: ElasticsearchAggregationBucketFragment) {
  if (selectedEntityType.value === v.key) {
    return {
      name: nuxtRoute.name,
      query: {
        text: text.value,
      },
    }
  }

  return {
    name: nuxtRoute.name,
    query: {
      type: v.key,
      text: text.value,
    },
  }
}

const shouldShowPopularServices = computed(() => {
  return (
    siteName === 'pharmago' && (!results.value || results.value.length === 0)
  )
})

const scrollToEl = ref<HTMLDivElement | null>(null)
const { onChangePage } = usePaginationChange(scrollToEl)

setBreadcrumbLinks()
setLanguageLinksFromRoute(query.value)
useRouteCache((c) => c.setCacheable().setMaxAge(600))
await renderPageDependencies()

const imageStyle = defineImageStyle({
  type: 'sizes',
  aspectRatio: 1,
  sizes: {
    sm: 768,
    md: 768,
    lg: 646,
    xl: 770,
  },
})
</script>

<script lang="ts">
export default {
  name: 'PageSearch',
}
</script>

<style lang="postcss">
.search-excerpt em {
  @apply bg-lime-light/25 not-italic;
}
</style>
